/**
 * /payroll-l2/earned-value/$projectId — SPI/CPI earned value metrics for a project.
 */
import { createFileRoute, useParams } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Paper,
  Typography,
} from "@mui/material";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";

import { api } from "@/lib/api";
import type { EarnedValueSnapshot } from "@/lib/types";
import { HelpHint } from "@/components/HelpHint";

export const Route = createFileRoute("/_auth/payroll-l2/earned-value_/$projectId")({
  component: EarnedValuePage,
});

function fmtMoney(v: string | null) {
  if (!v) return "—";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(Number(v));
}

function fmtHours(v: string | null) {
  if (!v) return "—";
  return `${Number(v).toFixed(1)}h`;
}

function IndexChip({ label, value }: { label: string; value: string | null }) {
  if (!value) return <Chip label={`${label}: —`} size="small" />;
  const n = Number(value);
  const color = n >= 1 ? "success" : n >= 0.9 ? "warning" : "error";
  const Icon = n >= 1 ? TrendingUpIcon : TrendingDownIcon;
  return (
    <Chip
      icon={<Icon />}
      label={`${label}: ${n.toFixed(3)}`}
      color={color}
      size="small"
    />
  );
}

function StatCard({ title, value }: { title: string; value: string }) {
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="caption" color="text.secondary">{title}</Typography>
      <Typography variant="h6" fontWeight={600}>{value}</Typography>
    </Paper>
  );
}

function EarnedValuePage() {
  const { projectId } = useParams({ strict: false }) as { projectId: string };

  const { data, isLoading, error } = useQuery({
    queryKey: ["payroll-l2", "earned-value", projectId],
    queryFn: async () => {
      const list = await api<EarnedValueSnapshot[]>(
        `/api/payroll-l2/projects/${projectId}/earned-value`,
      );
      // Backend returns list of snapshots; show the most recent.
      return list?.[0] ?? null;
    },
    enabled: !!projectId,
  });

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
        <Typography variant="h5" fontWeight={600}>Earned Value</Typography>
        <HelpHint docPath="payroll-l2/earned-value.md" label="Earned value help" />
      </Box>
      <Typography variant="body2" color="text.secondary" mb={3} sx={{ fontFamily: "monospace" }}>
        Project: {projectId}
      </Typography>

      {isLoading && <CircularProgress size={24} />}
      {error && <Alert severity="error">Failed to load earned value snapshot.</Alert>}

      {data && (
        <>
          <Typography variant="caption" color="text.secondary">
            Snapshot date: {data.snapshot_date}
          </Typography>

          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mt: 1, mb: 3 }}>
            {([
              { title: "Budgeted Hours", value: fmtHours(data.budgeted_hours) },
              { title: "Actual Hours", value: fmtHours(data.actual_hours) },
              { title: "Budgeted Cost", value: fmtMoney(data.budgeted_cost) },
              { title: "Actual Cost", value: fmtMoney(data.actual_cost) },
              { title: "Earned Value", value: fmtMoney(data.earned_value) },
              { title: "EAC", value: fmtMoney(data.eac) },
            ] as { title: string; value: string }[]).map((s) => (
              <Box key={s.title} sx={{ flexBasis: { xs: "calc(50% - 8px)", sm: "calc(25% - 12px)" }, flexGrow: 1 }}>
                <StatCard title={s.title} value={s.value} />
              </Box>
            ))}
          </Box>

          <Box display="flex" gap={1} flexWrap="wrap">
            <IndexChip label="SPI" value={data.spi} />
            <IndexChip label="CPI" value={data.cpi} />
          </Box>

          {data.spi && Number(data.spi) < 0.9 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              SPI is below 0.90 — project is behind schedule. Review labor allocation.
            </Alert>
          )}
          {data.cpi && Number(data.cpi) < 0.9 && (
            <Alert severity="error" sx={{ mt: 2 }}>
              CPI is below 0.90 — project is over budget. EAC has been revised upward.
            </Alert>
          )}
        </>
      )}
    </Box>
  );
}
